import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

/* ------------- slices / thunks ------------- */
import {
  updateWizardStep,
  updateWizardDraft,
  resetWizardDraft,
  clearCampaignError,
} from "../../../store/slices/campaignSlice";

import {
  createCampaignThunk,
  fetchCustomersBySegmentsThunk,
  fetchSegmentStatsThunk,
} from "../../../store/thunks/campaignThunks";
import { fetchTemplatesThunk } from "../../../store/thunks/templateThunks";

/* ------------- UI / helpers ------------- */
import Stepper from "../../../components/UI/StepperModal";
import CampaignAudienceStep from "../components/CampaignAudienceStep";
import CampaignContentStep from "../components/CampaignContentStep";
import CampaignScheduleStep from "../components/CampaignScheduleStep";
import CampaignReviewStep from "../components/CampaignReviewStep";
import Button from "../../../components/common/Button";
import {
  notifySuccess,
  notifyError,
  notifyInfo,
} from "../../../utils/notification";
import { NOTIFICATION_ROUTES } from "../../../constants/notification.constant";
import { ArrowLeftIcon } from "@heroicons/react/outline";

const CampaignWizard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  /* ------------- redux state ------------- */
  const { wizardDraft, loading, error, lastCampaignResponse, customer } =
    useSelector((state) => state.notificationCampaigns);
  const { list: templates } = useSelector(
    (state) => state.notificationTemplates
  );

  /* NEW: customer slice */
  const {
    segmentStats,
    customerIds: cachedCustomerIds,
    isTestMode,
    loading: customerLoading,
  } = useSelector((state) => state.notificationCampaigns?.customer);

  /* ------------- local state ------------- */
  const [isModalOpen, setIsModalOpen] = useState(true);

  /* ------------- bootstrap ------------- */
  useEffect(() => {
    dispatch(fetchTemplatesThunk({ archived: false }));
    return () => dispatch(resetWizardDraft());
  }, [dispatch]);

  /* ------------- watch errors ------------- */
  useEffect(() => {
    if (error) {
      notifyError(error);
      dispatch(clearCampaignError());
    }
  }, [error, dispatch]);

  /* ------------- queuedUntil toast ------------- */
  useEffect(() => {
    if (lastCampaignResponse?.queuedUntil) {
      const t = new Date(lastCampaignResponse.queuedUntil);
      notifyInfo(
        `Campaign queued due to quiet hours. Sends at ${t.toLocaleTimeString()}`
      );
    }
  }, [lastCampaignResponse]);

  /* ------------- fetch segment HEAD-COUNTS whenever selection changes ------------- */
  useEffect(() => {
    if (wizardDraft.audience.segments.length) {
      dispatch(fetchSegmentStatsThunk(wizardDraft.audience.segments));
    }
  }, [wizardDraft.audience.segments, dispatch]);

  /* ------------- stepper helpers ------------- */
  const steps = [
    { label: "Audience", key: "audience" },
    { label: "Content", key: "content" },
    { label: "Schedule", key: "schedule" },
    { label: "Review", key: "review" },
  ];

  const handleNext = () => {
    const cur = wizardDraft.step;
    if (validateStep(cur)) dispatch(updateWizardStep(cur + 1));
  };

  const handlePrevious = () => dispatch(updateWizardStep(wizardDraft.step - 1));

  const handleClose = () => {
    if (
      window.confirm(
        "Are you sure you want to cancel? All progress will be lost."
      )
    ) {
      setIsModalOpen(false);
      navigate(NOTIFICATION_ROUTES.ROOT);
    }
  };

  /* ------------- validation by step ------------- */
  const validateStep = (step) => {
    switch (step) {
      case 1: {
        const { segments, testDevices } = wizardDraft.audience;
        if (!segments.length && !testDevices.length) {
          notifyError("Select at least one segment or test-device");
          return false;
        }
        return true;
      }
      case 2: {
        /* content step --------------------------------------------------- */
        const { content } = wizardDraft;
        if (content.templateKey) {
          const tpl = templates.find((t) => t.key === content.templateKey);
          const reqVars = tpl?.variables?.filter((v) => v.required) || [];
          const missing = reqVars.filter((v) => !content.variables[v.name]);
          if (missing.length) {
            notifyError(
              `Fill required variables: ${missing
                .map((v) => v.name)
                .join(", ")}`
            );
            return false;
          }
        } else if (!content.rawTitle || !content.rawBody) {
          notifyError("Provide both title and body for custom content");
          return false;
        }
        return true;
      }
      case 3: {
        /* schedule step -------------------------------------------------- */
        const { sendNow, scheduledTime } = wizardDraft.schedule;
        if (!sendNow && !scheduledTime) {
          notifyError("Choose when to send the campaign");
          return false;
        }
        return true;
      }
      default:
        return true;
    }
  };

  /* ------------- backend payload formatter ------------- */
  const transformDataForBackend = (draft) => {
    const { audience, content, schedule } = draft;
    const payload = {
      scheduleAt: schedule.sendNow ? undefined : schedule.scheduledTime,
      channels: audience.platforms.includes("all")
        ? ["FCM", "APNS", "WEB"]
        : audience.platforms.map((p) =>
            p.toLowerCase() === "ios"
              ? "APNS"
              : p.toLowerCase() === "android"
              ? "FCM"
              : p.toLowerCase() === "web"
              ? "WEB"
              : p.toUpperCase()
          ),
    };

    if (content.templateKey) {
      payload.templateKey = content.templateKey;
      payload.templateVersion = +content.templateVersion;
      payload.data = content.variables || {};

      const tpl = templates.find((t) => t.key === content.templateKey);
      if (tpl) payload.isPromotional = tpl.isPromotional;
    } else {
      Object.assign(payload, {
        rawTitle: content.rawTitle,
        rawBody: content.rawBody,
        rawImageUrl: content.rawImageUrl || undefined,
        rawIconUrl: content.rawIconUrl || undefined,
        rawDeeplink: content.rawDeeplink || undefined,
        rawActions: (content.rawActions || []).map(({ label, deeplink }) => ({
          label,
          deeplink,
        })),
        isPromotional: !!content.isPromotional,
      });
    }
    return payload;
  };

  /* ------------- SUBMIT ------------- */
  const handleSubmit = async () => {
    try {
      const { segments, testDevices } = wizardDraft.audience;

      /* 1️⃣ Resolve actual customer IDs */
      const { customerIds, count } = await dispatch(
        fetchCustomersBySegmentsThunk({ segments, testDevices })
      ).unwrap();

      if (!count) {
        notifyError("No recipients found for selected segments");
        return;
      }

      /* 2️⃣ Send campaign */
      const payload = {
        ...transformDataForBackend(wizardDraft),
        customerIds,
      };

      await dispatch(createCampaignThunk(payload)).unwrap();

      /* success toast handled by slice effect */
      navigate(NOTIFICATION_ROUTES.JOBS);
    } catch (err) {
      // error toasts handled by slice
      console.error("Campaign creation failed", err);
    }
  };

  /* ------------- render current step ------------- */
  const renderStepContent = () => {
    switch (wizardDraft.step) {
      case 1:
        return (
          <CampaignAudienceStep
            data={wizardDraft.audience}
            segmentStats={segmentStats}
            onChange={(upd) =>
              dispatch(updateWizardDraft({ section: "audience", data: upd }))
            }
          />
        );

      case 2:
        return (
          <CampaignContentStep
            data={wizardDraft.content}
            templates={templates}
            onChange={(upd) =>
              dispatch(updateWizardDraft({ section: "content", data: upd }))
            }
          />
        );

      case 3:
        return (
          <CampaignScheduleStep
            data={wizardDraft.schedule}
            onChange={(upd) =>
              dispatch(updateWizardDraft({ section: "schedule", data: upd }))
            }
          />
        );

      case 4: {
        const tpl = templates.find(
          (t) => t.key === wizardDraft.content.templateKey
        );
        return <CampaignReviewStep wizardDraft={wizardDraft} template={tpl} />;
      }

      default:
        return null;
    }
  };

  return (
    <div className="container mx-auto p-4">
      {/* header */}
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate(NOTIFICATION_ROUTES.ROOT)}
          className="flex items-center gap-2"
        >
          <ArrowLeftIcon className="w-4 h-4" /> Back
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">Create Campaign</h1>
          <p className="text-gray-600">Send targeted push notifications</p>
        </div>
      </div>

      {/* Stepper (no longer in a modal) */}
      <Stepper
        title="Create Push Notification Campaign"
        steps={steps}
        currentStep={wizardDraft.step}
        onNext={handleNext}
        onPrevious={handlePrevious}
        onSubmit={handleSubmit}
        onCancel={() => {
          if (
            window.confirm(
              "Are you sure you want to cancel? All progress will be lost."
            )
          ) {
            navigate(NOTIFICATION_ROUTES.ROOT);
          }
        }}
        loading={loading || customerLoading}
      >
        {renderStepContent()}
      </Stepper>
    </div>
  );
};

export default CampaignWizard;
